import { ref, onUnmounted } from 'vue';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { useAuth } from './useAuth';
import { AppAuthTokenKey } from '@/libs/constant';

interface ReviewSocketData {
  employees: Array<{
    _id: string;
    fullName: string;
  }>;
}

export enum REVIEW_SOCKET_EVENTS {
  // Events from receptionist
  REQUEST_REVIEW = 'request_review',
  CANCEL_REVIEW = 'cancel_review',

  // Events from customer
  REVIEW_SUBMITTED = 'review_submitted',
  REVIEW_CANCELLED = 'review_cancelled',

  // Events to client
  SHOW_REVIEW_FORM = 'show_review_form',
  HIDE_REVIEW_FORM = 'hide_review_form',
  REVIEW_RECEIVED = 'review_received',

  // Connection events
  RECEPTIONIST_CONNECTED = 'receptionist_connected',
  CUSTOMER_SCREEN_CONNECTED = 'customer_screen_connected',
  CLIENT_DISCONNECTED = 'client_disconnected',
}

export const useReviewSocket = () => {
  const { getAuthCookieByKey } = useAuth();
  const token = getAuthCookieByKey(AppAuthTokenKey);
  const socket = ref<Socket | null>(null);
  const isConnected = ref(false);
  const error = ref<string | null>(null);

  const connect = () => {
    if (socket.value?.connected) {
      return socket.value;
    }

    try {
      socket.value = io(`${import.meta.env.VITE_SOCKET_URL}`, {
        auth: {
          token
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });

      socket.value.on('connect', () => {
        isConnected.value = true;
        error.value = null;
        console.log('Socket connected to reviews namespace');
      });

      socket.value.on('disconnect', () => {
        isConnected.value = false;
        console.log('Socket disconnected from reviews namespace');
      });

      socket.value.on('connect_error', (err) => {
        error.value = err.message;
        console.error('Socket connection error:', err);
      });

      socket.value.on('error', (err) => {
        error.value = err.message;
        console.error('Socket error:', err);
      });

      return socket.value;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to create socket:', err);
      return null;
    }
  };

  const disconnect = () => {
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
    }
  };


  const startReviewSession = (data: ReviewSocketData) => {
    if (!socket.value?.connected) {
      connect();
    }

    socket.value?.emit(REVIEW_SOCKET_EVENTS.REQUEST_REVIEW, data, (response: any) => {
      if (response?.success) {
        console.log('Review session started:', response);
      } else {
        console.error('Failed to start review session:', response);
      }
    });
  };


  const onReviewSessionStart = (callback: (data: ReviewSocketData) => void) => {
    if (!socket.value?.connected) {
      connect();
    }
    console.log('onReviewSessionStart', REVIEW_SOCKET_EVENTS.SHOW_REVIEW_FORM);
    socket.value?.on(REVIEW_SOCKET_EVENTS.SHOW_REVIEW_FORM, callback);
  };

  const submitReview = (reviewData: any) => {
    if (!socket.value?.connected) {
      console.error('Socket not connected');
      return;
    }

    socket.value.emit(REVIEW_SOCKET_EVENTS.REVIEW_SUBMITTED, reviewData, (response: any) => {
      if (response?.success) {
        console.log(' Review submitted:', response);
      } else {
        console.error(' Failed to submit review:', response);
      }
    });
  };

  const onReviewSubmitted = (callback: (data: any) => void) => {
    if (!socket.value?.connected) {
      connect();
    }
    socket.value?.on(REVIEW_SOCKET_EVENTS.REVIEW_SUBMITTED, callback);
  };

  const removeAllListeners = () => {
    socket.value?.off('customer:review-started');
    socket.value?.off('customer:review-submitted');
  };

  onUnmounted(() => {
    removeAllListeners();
    disconnect();
  });

  return {
    socket,
    isConnected,
    error,
    connect,
    disconnect,
    startReviewSession,
    onReviewSessionStart,
    submitReview,
    onReviewSubmitted,
    removeAllListeners
  };
};

